import React from 'react';
import Card from '../design-system/Card';
import { colors, spacing, fontStack } from '../design-system/tokens';

/**
 * CruxInsight — the centerpiece insight surface used on every page.
 *
 * Props:
 *   - state: string (1-2 sentences, current condition)
 *   - actions: string[] (2-4 bullets — the "do this")
 *   - reasons: string[] (1-3 bullets — the "why")
 *   - accent: optional accent color (defaults to primary)
 *   - confidence: optional number 0-100 (shown top-right)
 *   - isLoading: optional boolean
 *   - wide: optional boolean — Copilot answer treatment. Adds bigger
 *     padding (40px desktop / 24px mobile) and an even 3-col grid with
 *     wider gap. Defaults false; the rest of the app keeps the
 *     compact 1.2/1/1 layout used since Phase 1.
 */
export default function CruxInsight({
  state,
  actions = [],
  reasons = [],
  accent = colors.primary,
  confidence,
  isLoading = false,
  wide = false,
}) {
  return (
    <Card accentColor={accent} padding={wide ? 40 : 28} className={wide ? 'crux-insight--wide' : undefined}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.base,
          fontFamily: fontStack,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SparkleIcon color={accent} />
          <h2
            style={{
              margin: 0,
              fontSize: 14,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: colors.textSecondary,
              fontWeight: 600,
            }}
          >
            Curx Insight
          </h2>
        </div>
        {typeof confidence === 'number' && (
          <div
            aria-label={`Confidence ${confidence}%`}
            style={{
              fontSize: 12,
              color: colors.textSecondary,
              fontFamily: fontStack,
              fontWeight: 500,
              letterSpacing: '0.04em',
            }}
          >
            {confidence}% confidence
          </div>
        )}
      </div>

      {isLoading ? (
        <div
          style={{
            color: colors.textSecondary,
            fontFamily: fontStack,
            fontSize: 15,
            padding: `${spacing.base}px 0`,
          }}
        >
          Curx is reading your world...
        </div>
      ) : (
        <div className="crux-insight-grid">
          <Section title="State">
            <StateBlock state={state} />
          </Section>
          <Section title="Do this">
            <ActionsList items={actions} accent={accent} />
          </Section>
          <Section title="Reason">
            <ReasonsList items={reasons} />
          </Section>
        </div>
      )}

      {/* Local responsive layout — 3-col desktop, stacked mobile.
          Inlined here so component is fully self-contained.
          The `.crux-insight--wide` variant (Copilot answer) gets even
          columns + a wider gap, plus a tightened mobile padding so the
          card doesn't feel cramped on phones. */}
      <style>{`
        .crux-insight-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr 1fr;
          gap: 32px;
        }
        .crux-insight--wide .crux-insight-grid {
          grid-template-columns: 1fr 1fr 1fr;
          gap: 48px;
        }
        @media (max-width: 1024px) {
          .crux-insight--wide .crux-insight-grid {
            gap: 32px;
          }
        }
        @media (max-width: 768px) {
          .crux-insight-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .crux-insight--wide {
            padding: 24px !important;
          }
          .crux-insight--wide .crux-insight-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
        }
      `}</style>
    </Card>
  );
}

/* --- Subcomponents --- */

function Section({ title, children }) {
  return (
    <div style={{ fontFamily: fontStack }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.textSecondary,
          fontWeight: 600,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function StateBlock({ state }) {
  if (!state) return null;
  // First sentence is bold, rest is regular.
  const match = state.match(/^([^.!?]+[.!?])\s*(.*)$/);
  const first = match ? match[1].trim() : state;
  const rest = match ? match[2].trim() : '';

  return (
    <p
      style={{
        margin: 0,
        fontSize: 17,
        lineHeight: 1.5,
        color: colors.textPrimary,
      }}
    >
      <span style={{ fontWeight: 600 }}>{first}</span>
      {rest && <span style={{ fontWeight: 400 }}> {rest}</span>}
    </p>
  );
}

function ActionsList({ items = [], accent }) {
  if (!items.length) return null;
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            color: colors.textPrimary,
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          <CheckBox color={accent} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ReasonsList({ items = [] }) {
  if (!items.length) return null;
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {items.map((item, i) => (
        <li
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            color: colors.textSecondary,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: colors.textSecondary,
              marginTop: 8,
              flexShrink: 0,
            }}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function CheckBox({ color }) {
  return (
    <span
      aria-hidden="true"
      style={{
        flexShrink: 0,
        width: 16,
        height: 16,
        borderRadius: 4,
        border: `1.5px solid ${color}`,
        marginTop: 2,
        display: 'inline-block',
      }}
    />
  );
}

function SparkleIcon({ color }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />
      <path d="M19 17l.8 2.2L22 20l-2.2.8L19 23l-.8-2.2L16 20l2.2-.8L19 17z" />
    </svg>
  );
}
